import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api as axios } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import './ActivityLog.css';

const ActivityLog = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states - only Site and Search
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination states
  const [page, setPage] = useState(1);
  const limit = 50; // Fixed limit
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Sorting states
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Expanded details state
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Refs for debouncing and request cancellation
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const lastFetchParamsRef = useRef('');

  // Fetch sites list
  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get('/api/sites');
      setSites(response.data || []);
    } catch (err) {
      console.error('[ActivityLog] Failed to load sites:', err);
    }
  }, []);


  const getLogTimestamp = (log) => {
    if (!log) return null;
    // Prefer original WP timestamp if present (plugin sends `timestamp` in mysql format)
    const rawTs = log?.meta?.raw?.timestamp || log?.meta?.timestamp || log?.timestamp || log?.createdAt;
    if (!rawTs) return null;
    // Handle WP mysql format: "YYYY-MM-DD HH:mm:ss"
    if (typeof rawTs === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(rawTs)) {
      return new Date(rawTs.replace(' ', 'T') + 'Z');
    }
    const d = new Date(rawTs);
    return isNaN(d.getTime()) ? null : d;
  };

  const getDisplayId = (log) => {
    return (
      log?.meta?.wp_occurrence_id ||
      log?.meta?.wp_activity_log_occurrence_id ||
      log?.meta?.wp_activity_log_id ||
      log?._id ||
      'N/A'
    );
  };

  // Extract user who performed the event (prioritize username over email)
  const getUser = (log) => {
    // Priority 1: Actor username (most reliable source)
    const actor = log?.actor;
    if (actor?.type === 'user' && actor?.username && actor.username !== 'unknown') {
      const role = actor?.role ? ` ${actor.role.charAt(0).toUpperCase() + actor.role.slice(1)}` : '';
      return `${actor.username}${role}`;
    }
    
    // Priority 2: Meta Username
    if (log?.meta?.Username) {
      const role = log?.meta?.UserRole || log?.meta?.CurrentUserRoles || log?.meta?.role;
      const roleStr = role ? ` ${role.charAt(0).toUpperCase() + role.slice(1)}` : '';
      return `${log.meta.Username}${roleStr}`;
    }
    
    // Priority 3: Direct username field
    if (log?.username) {
      const role = log?.user_role || log?.meta?.role;
      const roleStr = role ? ` ${role.charAt(0).toUpperCase() + role.slice(1)}` : '';
      return `${log.username}${roleStr}`;
    }
    
    // Priority 4: Actor userId object
    if (actor?.type === 'user' && actor?.userId) {
      const user = actor.userId;
      if (typeof user === 'object') {
        if (user.username) {
          const role = actor?.role ? ` ${actor.role.charAt(0).toUpperCase() + actor.role.slice(1)}` : '';
          return `${user.username}${role}`;
        }
        if (user.display_name) return user.display_name;
        if (user.email) return user.email;
      }
    }
    
    // Priority 5: Display name from meta
    if (log?.meta?.DisplayName) {
      return log.meta.DisplayName;
    }
    
    // Priority 6: Email as last resort
    if (log?.meta?.Email) {
      // Extract username from email if possible
      const email = log.meta.Email;
      const emailParts = email.split('@');
      if (emailParts[0]) {
        return emailParts[0];
      }
      return email;
    }
    
    // Priority 7: Check for system/scanner/api actors
    if (actor?.type === 'scanner') {
      return 'Security Scanner';
    }
    if (actor?.type === 'api') {
      return 'API';
    }
    if (actor?.type === 'system' || !actor) {
      return 'System';
    }
    
    return 'N/A';
  };

  // Helper function to check if log matches search query
  const matchesSearch = (log, query) => {
    if (!query || query.trim() === '') return true;
    
    const searchLower = query.toLowerCase().trim();
    
    // Search in Module
    const module = (log.module || '').toLowerCase();
    if (module.includes(searchLower)) return true;
    
    // Search in Severity
    const severity = (log.severity || '').toLowerCase();
    if (severity.includes(searchLower)) return true;
    
    // Search in Event Type / Action
    const eventType = ((log.action || log.eventType || '') + '').toLowerCase();
    if (eventType.includes(searchLower)) return true;
    
    // Search in Object
    const object = ((log.object || log.meta?.Object || '') + '').toLowerCase();
    if (object.includes(searchLower)) return true;
    
    // Search in User
    const user = getUser(log).toLowerCase();
    if (user.includes(searchLower)) return true;
    
    // Search in Message / Description
    const message = ((log.message || log.description || '') + '').toLowerCase();
    if (message.includes(searchLower)) return true;
    
    return false;
  };

  // Fetch audit logs with request cancellation and throttling
  const fetchAuditLogs = useCallback(async (force = false) => {
    // Create a unique key for current fetch parameters
    const fetchKey = `${selectedSiteId}-${page}-${limit}-${sortBy}-${sortOrder}-${searchQuery}`;
    
    // Prevent duplicate requests with same parameters (unless forced)
    if (!force && fetchKey === lastFetchParamsRef.current) {
      const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
      // Throttle: Don't fetch if same params and less than 2 seconds ago
      if (timeSinceLastFetch < 2000) {
        return;
      }
    }
    
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Prevent overlapping requests
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    abortControllerRef.current = new AbortController();
    lastFetchParamsRef.current = fetchKey;
    lastFetchTimeRef.current = Date.now();
    
    try {
      if (!selectedSiteId) {
        // If no site selected, fetch ALL activity logs in one request (avoids site deduplication gaps)
        setLoading(true);
        setError(null);
        
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('limit', '2000'); // Fetch enough for client-side filtering and pagination
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);
        params.set('_t', Date.now().toString()); // Cache-bust: prevent 304 Not Modified / stale data

        const response = await axios.get(`/api/wp-activity?${params.toString()}`, {
          signal: abortControllerRef.current.signal,
          timeout: 20000,
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        // Handle various response shapes (direct array, activityLogs, or data.activityLogs)
        let allLogs = response.data?.activityLogs ?? response.data?.data?.activityLogs ?? 
          (Array.isArray(response.data) ? response.data : []);
        if (!Array.isArray(allLogs)) {
          allLogs = allLogs && typeof allLogs === 'object' ? [allLogs] : [];
        }
        
        // Filter out backend/system events
        allLogs = allLogs.filter(log => {
          if (log.action && log.action.startsWith('BACKEND_')) return false;
          if (log.module === 'SYSTEM') return false;
          const objectValue = (log.object || (log.meta && log.meta.Object) || '').toString().toUpperCase();
          if (objectValue === 'REST API') return false;
          if (!matchesSearch(log, searchQuery)) return false;
          return true;
        });
        
        // Sort combined logs (newest first by default). When timestamps are equal,
        // enforce plugin event priority so that Activated > Installed > Uploaded,
        // matching WP Activity Log semantics.
        allLogs.sort((a, b) => {
          const ta = new Date(a.createdAt || a.timestamp || 0).getTime();
          const tb = new Date(b.createdAt || b.timestamp || 0).getTime();

          if (ta !== tb) {
            return sortOrder === 'desc' ? tb - ta : ta - tb;
          }

          const priorityFor = (log) => {
            const type = (log.eventType || log.meta?.event_type_display || log.action || '').toString().toUpperCase();
            if (type.includes('ACTIVATED')) return 3;
            if (type.includes('INSTALLED')) return 2;
            if (type.includes('UPLOADED')) return 1;
            return 0;
          };

          const pa = priorityFor(a);
          const pb = priorityFor(b);
          if (pa !== pb) {
            // Higher priority first
            return sortOrder === 'desc' ? pb - pa : pa - pb;
          }

          return 0;
        });

        // Hide noisy POST_OPENED / POST_MODIFIED events that immediately
        // precede a POST_DELETED for the same post, to match WP Activity Log
        // behaviour (which only shows "Deleted" in that flow).
        const deleteByPost = new Map();
        allLogs.forEach(log => {
          const actionStr = (log.action || log.eventType || '').toString().toUpperCase();
          const isPostDelete = actionStr.includes('POST_DELETED');
          const postId = log.meta?.PostID;
          if (isPostDelete && postId) {
            const ts = new Date(log.createdAt || log.timestamp || 0).getTime();
            const prev = deleteByPost.get(postId);
            if (!prev || ts > prev) {
              deleteByPost.set(postId, ts);
            }
          }
        });

        allLogs = allLogs.filter(log => {
          const actionStr = (log.action || log.eventType || '').toString().toUpperCase();
          const isOpenOrModified =
            actionStr.includes('POST_OPENED') || actionStr.includes('POST_MODIFIED');
          if (!isOpenOrModified) return true;

          const postId = log.meta?.PostID;
          if (!postId) return true;

          const deleteTs = deleteByPost.get(postId);
          if (!deleteTs) return true;

          const ts = new Date(log.createdAt || log.timestamp || 0).getTime();
          // If within 5 seconds of delete, hide it
          return Math.abs(ts - deleteTs) > 5000;
        });

        // Apply pagination (no client-side deduplication - display all events from API)
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedLogs = allLogs.slice(startIndex, endIndex);
        
        setAuditLogs(paginatedLogs);
        setTotal(allLogs.length);
        setTotalPages(Math.ceil(allLogs.length / limit));
        setLoading(false);
      } else {
        // Use site-specific API when a site is selected - ensures correct site filtering
        // and proper server-side sort (newest received first)
        setError(null);
        setLoading(true);
        
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('limit', '2000');
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);
        params.set('_t', Date.now().toString()); // Cache-bust: prevent 304 Not Modified / stale data

        const response = await axios.get(`/api/sites/${selectedSiteId}/wp-activity?${params.toString()}`, {
          signal: abortControllerRef.current.signal,
          timeout: 20000,
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        // Handle various response shapes (direct array, activityLogs, or data.activityLogs)
        let allLogs = response.data?.activityLogs ?? response.data?.data?.activityLogs ??
          (Array.isArray(response.data) ? response.data : []);
        if (!Array.isArray(allLogs)) {
          allLogs = allLogs && typeof allLogs === 'object' ? [allLogs] : [];
        }
        
        // Filter out backend/system events and apply search
        allLogs = allLogs.filter(log => {
          if (log.action && log.action.startsWith('BACKEND_')) return false;
          if (log.module === 'SYSTEM') return false;
          const objectValue = (log.object || (log.meta && log.meta.Object) || '').toString().toUpperCase();
          if (objectValue === 'REST API') return false;
          if (!matchesSearch(log, searchQuery)) return false;
          return true;
        });
        
        // Sort by date descending (newest first). For equal timestamps, ensure
        // Activated > Installed > Uploaded within plugin events to mirror
        // WP Activity Log ordering.
        allLogs.sort((a, b) => {
          const ta = new Date(a.createdAt || a.timestamp || 0).getTime();
          const tb = new Date(b.createdAt || b.timestamp || 0).getTime();

          if (ta !== tb) {
            return tb - ta;
          }

          const priorityFor = (log) => {
            const type = (log.eventType || log.meta?.event_type_display || log.action || '').toString().toUpperCase();
            if (type.includes('ACTIVATED')) return 3;
            if (type.includes('INSTALLED')) return 2;
            if (type.includes('UPLOADED')) return 1;
            return 0;
          };

          const pa = priorityFor(a);
          const pb = priorityFor(b);
          if (pa !== pb) {
            return pb - pa;
          }

          return 0;
        });

        // Hide POST_OPENED / POST_MODIFIED right before POST_DELETED for same post
        const deleteByPostSingle = new Map();
        allLogs.forEach(log => {
          const actionStr = (log.action || log.eventType || '').toString().toUpperCase();
          const isPostDelete = actionStr.includes('POST_DELETED');
          const postId = log.meta?.PostID;
          if (isPostDelete && postId) {
            const ts = new Date(log.createdAt || log.timestamp || 0).getTime();
            const prev = deleteByPostSingle.get(postId);
            if (!prev || ts > prev) {
              deleteByPostSingle.set(postId, ts);
            }
          }
        });

        allLogs = allLogs.filter(log => {
          const actionStr = (log.action || log.eventType || '').toString().toUpperCase();
          const isOpenOrModified =
            actionStr.includes('POST_OPENED') || actionStr.includes('POST_MODIFIED');
          if (!isOpenOrModified) return true;

          const postId = log.meta?.PostID;
          if (!postId) return true;

          const deleteTs = deleteByPostSingle.get(postId);
          if (!deleteTs) return true;

          const ts = new Date(log.createdAt || log.timestamp || 0).getTime();
          return Math.abs(ts - deleteTs) > 5000;
        });

        // Apply pagination
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedLogs = allLogs.slice(startIndex, endIndex);
        
        setAuditLogs(paginatedLogs);
        setTotal(allLogs.length);
        setTotalPages(Math.ceil(allLogs.length / limit));
        setLoading(false);
      }
    } catch (err) {
      // Don't show error if request was aborted
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        isFetchingRef.current = false;
        setLoading(false);
        return;
      }
      console.error('[ActivityLog] Failed to fetch audit logs', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load activity logs';
      setError(errorMsg);
      setAuditLogs([]);
      setLoading(false);
    } finally {
      isFetchingRef.current = false;
      abortControllerRef.current = null;
    }
  }, [selectedSiteId, page, limit, sortBy, sortOrder, searchQuery, sites]);

  // Fetch sites on mount
  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // Single unified effect to handle all filter changes with debouncing
  useEffect(() => {
    // Both use /api/wp-activity; single site filters client-side by siteId/URL.
    
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce search input (500ms delay)
    if (searchQuery) {
      debounceTimerRef.current = setTimeout(() => {
        if (!isFetchingRef.current) {
          fetchAuditLogs(true); // Force fetch after debounce
        }
      }, 500);
    } else {
      // Immediate fetch for site selection, pagination, etc. (but only if not already fetching)
      if (!isFetchingRef.current) {
        fetchAuditLogs(true);
      }
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    selectedSiteId,
    page,
    sortBy,
    sortOrder,
    searchQuery,
    sites.length,
    fetchAuditLogs
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Auto-refresh logs periodically so new WordPress events appear in near real-time
  useEffect(() => {
    const interval = setInterval(() => {
      // Avoid overlapping requests
      if (!isFetchingRef.current) {
        fetchAuditLogs(true);
      }
    }, 5000); // 5 seconds - periodic refresh for live activity display

    return () => clearInterval(interval);
  }, [fetchAuditLogs]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleFilterChange = () => {
    setPage(1); // Reset to first page when filters change
  };

  const getSeverityColor = (severity) => {
    const colors = {
      info: '#28a745',
      warning: '#f59e0b', // Orange color for warning triangle
      high: '#fd7e14',
      critical: '#dc3545',
    };
    return colors[severity] || '#6c757d';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    // Format: "February 3, 2026 at 5:20:11.000 am"
    // (Don't do a naive comma replace; it breaks ordering like "February 3 at 2026 ...")
    const datePart = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    const formattedWithAt = `${datePart} at ${timePart}`;
    // Add milliseconds
    const ms = date.getMilliseconds();
    return formattedWithAt.replace(/(\d+:\d+:\d+)/, `$1.${ms.toString().padStart(3, '0')}`);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    // Format: "February 3, 2026 at 5:20:11.000 am"
    return formatTime(timestamp);
  };

  const formatUser = (actor, log) => {
    // Try to get user info from actor or log metadata
    if (actor) {
      if (actor.type === 'user' && actor.username) {
        const role = actor.role ? ` ${actor.role.charAt(0).toUpperCase() + actor.role.slice(1)}` : '';
        return `${actor.username}${role}`;
      }
      if (actor.type === 'user' && actor.userId) {
        const user = actor.userId;
        if (typeof user === 'object' && user.username) {
          const role = actor.role ? ` ${actor.role.charAt(0).toUpperCase() + actor.role.slice(1)}` : '';
          return `${user.username}${role}`;
        }
        return `User (${actor.userId})`;
      }
    }
    
    // Fallback to log metadata
    if (log?.meta?.Username) {
      const role = log.meta.UserRole ? ` ${log.meta.UserRole}` : '';
      return `${log.meta.Username}${role}`;
    }
    
    if (log?.username) {
      const role = log.user_role ? ` ${log.user_role}` : '';
      return `${log.username}${role}`;
    }
    
    if (actor?.type === 'scanner') {
      return 'Security Scanner';
    }
    if (actor?.type === 'api') {
      return 'API';
    }
    return 'System';
  };

  const formatIP = (log) => {
    let ip =
      (log && log.meta && log.meta.ClientIP) ||
      log?.ip_address ||
      (log && log.meta && log.meta.IP) ||
      log?.actor?.ipAddress ||
      '';

    if (ip === '::1') {
      ip = '127.0.0.1';
    }

    if (ip) return ip;
    // For plugin_inventory (backend scan) events, show site host for context
    if (log?.meta?.inferredFrom === 'plugin_inventory') {
      const url = log?.meta?.SiteURL || getWebsite(log);
      if (url) {
        try {
          const host = new URL(url).hostname;
          return host || 'N/A';
        } catch (_) {
          return 'N/A';
        }
      }
    }
    return 'N/A';
  };

  const formatObject = (log) => {
    // Get object type from log
    if (log?.object) return log.object;
    if (log?.meta?.Object) return log.meta.Object;
    return 'N/A';
  };

  const formatEventType = (log) => {
    // Get event type/action from log
    if (log?.action) return log.action;
    if (log?.event_type) return log.event_type;
    return 'N/A';
  };

  const getSeverityIcon = (severity) => {
    // Return icon based on severity (orange warning triangle for warning, etc.)
    const severityLower = (severity || 'info').toLowerCase();
    if (severityLower === 'warning') {
      return '⚠'; // Orange warning triangle
    } else if (severityLower === 'info') {
      return 'ℹ'; // Info icon
    } else if (severityLower === 'high' || severityLower === 'critical') {
      return '🔴'; // Red circle
    }
    return 'ℹ';
  };

  const getSiteName = (siteId) => {
    if (!siteId) return 'N/A';
    const site = sites.find(s => s._id === siteId);
    return site ? site.name : 'Unknown Site';
  };

  const toggleDetails = (logId) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  // Helper functions for Reports-style table formatting
  const formatDateForPdf = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const isUpToDateNoopLog = (log) => {
    const messageLower = (log?.message || '').toString().toLowerCase();
    const act = (log?.action || '').toString().toLowerCase();
    return (
      act === 'update_noop' ||
      messageLower.includes('already up to date') ||
      messageLower.includes('already up-to-date')
    );
  };

  // Get status color class based on action + message
  const getStatusClass = (action, log) => {
    const act = (action || '').toLowerCase();
    if (isUpToDateNoopLog(log)) return 'status-success';
    if (act.includes('success') || 
        act === 'activated' || 
        act.includes('plugin_activated') || 
        act.includes('theme_activated') ||
        act.includes('published') ||
        act.includes('created') && !act.includes('failed')) return 'status-success';
    if (act.includes('failed') || act.includes('error') || act.includes('delete')) return 'status-critical';
    if (act === 'deactivated' || 
        act.includes('plugin_deactivated') || 
        act.includes('theme_deactivated') ||
        act.includes('trash')) return 'status-warning';
    if (act.includes('update') || 
        act.includes('updated') || 
        act.includes('plugin_updated') ||
        act.includes('theme_updated') ||
        act.includes('modified')) return 'status-info';
    // Default to info for other events
    return 'status-info';
  };

  // Get status text
  const getStatusText = (action, log) => {
    const act = (action || '').toLowerCase();
    if (isUpToDateNoopLog(log)) return 'Success';
    if (act.includes('success')) return 'Success';
    if (act.includes('failed') || act.includes('error')) return 'Failed';
    if (act === 'activated' || act.includes('plugin_activated') || act.includes('theme_activated')) return 'Activated';
    if (act === 'deactivated' || act.includes('plugin_deactivated') || act.includes('theme_deactivated')) return 'Deactivated';
    if (act === 'enabled_auto_update' || act.includes('enabled_auto_update')) return 'Enabled Auto Update';
    if (act === 'disabled_auto_update' || act.includes('disabled_auto_update')) return 'Disabled Auto Update';
    if (act === 'update_available' || act.includes('update_available')) return 'Update';
    if (act === 'update_noop' || act.includes('update_noop')) return 'Success';
    if (act === 'update_success' || 
        act.includes('update_success') || 
        act.includes('plugin_updated') ||
        act.includes('theme_updated')) return 'Updated';
    if (act.includes('update')) return 'Update';
    if (act.includes('published')) return 'Published';
    if (act.includes('created')) return 'Created';
    if (act.includes('modified')) return 'Modified';
    if (act.includes('deleted') || act.includes('delete')) return 'Deleted';
    if (act.includes('restored')) return 'Restored';
    if (act.includes('trash')) return 'Trashed';
    // Return formatted action name
    return action ? action.replace(/_/g, ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ') : '—';
  };

  // Extract plugin name from log
  const getPluginName = (log) => {
    // Check if this is a plugin-related event
    const isPluginEvent = log?.object === 'Plugin' || 
                         (log?.action && (log.action.toLowerCase().includes('plugin') || log.action.toLowerCase().includes('theme'))) ||
                         (log?.meta?.PluginName) ||
                         (log?.description && log.description.toLowerCase().includes('plugin'));
    
    if (!isPluginEvent) {
      // For Post/Page events, show "Post: title" or "Page: title" when available
      const obj = log?.object || log?.meta?.Object;
      const title = log?.meta?.PostTitle;
      if (obj && (obj === 'Post' || obj === 'Page') && title) {
        return `${obj}: ${title}`;
      }
      if (obj) return obj;
      return 'N/A';
    }
    
    if (log?.meta?.PluginName) return log.meta.PluginName;
    if (log?.meta?.ThemeName) return log.meta.ThemeName;
    if (log?.object === 'Plugin' && log?.meta?.object) return log.meta.object;
    if (log?.description) {
      // Try to extract plugin name from description like "Plugin 'Akismet Anti-spam: Spam Protection' activated"
      const match = log.description.match(/(?:Plugin|Theme)\s+['"]([^'"]+)['"]/i);
      if (match) return match[1];
    }
    if (log?.message) {
      const match = log.message.match(/(?:Plugin|Theme)\s+['"]([^'"]+)['"]/i);
      if (match) return match[1];
    }
    return log?.object === 'Plugin' ? 'Unknown Plugin' : (log?.object || 'N/A');
  };

  // Extract website URL from log
  const getWebsite = (log) => {
    if (log?.siteUrl) return log.siteUrl;
    if (log?.meta?.SiteURL) return log.meta.SiteURL;
    // Try to get from sites list if we have siteId (compare as strings for consistency)
    if (log?.siteId) {
      const siteIdStr = String(log.siteId);
      const site = sites.find(s => String(s._id) === siteIdStr);
      if (site?.url) return site.url;
    }
    return 'N/A';
  };

  // Extract version information from log
  const getVersion = (log) => {
    // Check for version range (old → new) - common in update events
    if (log?.meta?.OldVersion && log?.meta?.NewVersion) {
      return `${log.meta.OldVersion} → ${log.meta.NewVersion}`;
    }
    if (log?.meta?.OldVersion && log?.meta?.PluginVersion) {
      return `${log.meta.OldVersion} → ${log.meta.PluginVersion}`;
    }
    // Check for single version
    if (log?.meta?.PluginVersion) return log.meta.PluginVersion;
    if (log?.meta?.ThemeVersion) return log.meta.ThemeVersion;
    if (log?.meta?.NewVersion) return log.meta.NewVersion;
    if (log?.meta?.OldVersion) return log.meta.OldVersion;
    // For non-plugin/theme events, return N/A
    return 'N/A';
  };

  return (
    <div className="activity-log-page">
      <Sidebar />
      <div className="activity-log-content">
        <div className="activity-log-header">
          <h1>Activity Log</h1>
          <p className="activity-log-subtitle">
            View all activity logs across your websites
          </p>
        </div>

        {/* Filters */}
        <div className="activity-log-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Site</label>
              <select
                value={selectedSiteId}
                onChange={(e) => {
                  setSelectedSiteId(e.target.value);
                  handleFilterChange();
                }}
                className="filter-select"
              >
                <option value="">All Sites</option>
                {sites.map(site => (
                  <option key={site._id} value={site._id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Search</label>
              <div className="search-input-wrapper">
                <span className="search-icon" aria-hidden="true"></span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Don't call handleFilterChange - debounced in useEffect
                  }}
                  placeholder="Search in Module, Severity, Event Type, Object, User, Messages..."
                  className="filter-input search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="activity-log-results">
          {loading && auditLogs.length === 0 ? (
            <div className="activity-log-loading">
              <p>Loading activity logs...</p>
            </div>
          ) : error ? (
            <div className="activity-log-error">
              <p>Error: {error}</p>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="activity-log-empty">
              <p><strong>No activity logs found.</strong></p>
              <div style={{ marginTop: '20px', padding: '20px', background: '#f9f9f9', borderRadius: '4px' }}>
                <p style={{ marginBottom: '10px' }}>This could mean:</p>
                <ul style={{ marginLeft: '20px', marginBottom: '10px' }}>
                  <li>No activity events have been sent from your WordPress sites yet</li>
                  <li>The WebMonitor Activity plugin is not installed or configured on your WordPress sites</li>
                  <li>Events are being filtered out by your current filter settings</li>
                  <li>Events haven't been synced from WordPress yet</li>
                  <li>The backend API might not have received any events yet</li>
                </ul>
                <p style={{ marginTop: '15px', marginBottom: '5px' }}><strong>To get activity logs:</strong></p>
                <ol style={{ marginLeft: '20px' }}>
                  <li>Install the <strong>WebMonitor Activity Logger</strong> plugin on your WordPress sites</li>
                  <li>Configure the plugin with your backend URL and Site ID (found in your WebMonitor dashboard)</li>
                  <li>Enable activity logging in the plugin settings</li>
                  <li>Perform some activities on your WordPress site (login, activate plugin, edit post, etc.)</li>
                  <li>Wait a few seconds for events to be queued and sent (plugin processes queue every 2 minutes or on shutdown)</li>
                  <li>Wait for auto-refresh (every 5 seconds)</li>
                </ol>
                <div style={{ marginTop: '15px', padding: '10px', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>Debug Tips:</p>
                  <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                    <li>Click <strong>Refresh</strong> to fetch the latest data from the server</li>
                    <li>Try selecting <strong>All Sites</strong> to see if events appear from other sites</li>
                    <li>Check browser console (F12) for any error messages</li>
                    <li>Verify the WebMonitor Activity plugin is configured with correct Backend URL and Site ID on your WordPress site</li>
                  </ul>
                </div>
                {selectedSiteId && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#e3f2fd', borderRadius: '4px' }}>
                    <p style={{ margin: 0 }}><strong>Tip:</strong> Try selecting "All Sites" from the Site filter to see logs from all your websites.</p>
                  </div>
                )}
                {!selectedSiteId && sites.length > 0 && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#e3f2fd', borderRadius: '4px' }}>
                    <p style={{ margin: 0 }}><strong>Tip:</strong> Try selecting a specific site from the Site filter to see logs for that site only.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="activity-log-table-wrapper reports-table-wrapper">
                <table className="activity-log-table reports-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('timestamp')} className="sortable">
                        Timestamp {sortBy === 'timestamp' && (sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('siteUrl')} className="sortable">
                        Website {sortBy === 'siteUrl' && (sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('object')} className="sortable">
                        Object {sortBy === 'object' && (sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('action')} className="sortable">
                        Event Type {sortBy === 'action' && (sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('ip_address')} className="sortable">
                        IP Address {sortBy === 'ip_address' && (sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('username')} className="sortable">
                        User {sortBy === 'username' && (sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('message')} className="sortable">
                        Message {sortBy === 'message' && (sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log, index) => {
                      const logId = log._id || log.id || `log-${index}`;
                      const isExpanded = expandedRow === logId;
                      const ts = getLogTimestamp(log) || log.createdAt;
                      const dateStr = ts ? formatDateForPdf(ts) : 'N/A';
                      const statusClass = getStatusClass(log.action, log);
                      const statusText = getStatusText(log.action, log);
                      const pluginName = getPluginName(log);
                      const website = getWebsite(log);
                      const version = getVersion(log);
                      const user = getUser(log);
                      
                      return (
                        <React.Fragment key={logId}>
                          <tr 
                            onClick={() => toggleDetails(logId)}
                            style={{ cursor: 'pointer' }}
                            className={isExpanded ? 'row-expanded' : ''}
                          >
                            <td>{dateStr}</td>
                            <td>{website}</td>
                            <td>{pluginName}</td>
                            <td className={`status-cell ${statusClass}`}>{statusText}</td>
                            <td>{formatIP(log)}</td>
                            <td>{user}</td>
                            <td className="reports-message-cell">
                              {log.message || log.description || '—'}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="7" className="expanded-details-cell">
                                <div className="activity-log-details">
                                  <div className="details-content">
                                    <h3>Event Details</h3>
                                    <div className="details-grid">
                                      <div className="detail-item">
                                        <strong>Event ID:</strong> {log._id}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Module:</strong> {log.module || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Action:</strong> {log.action || log.eventType || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Event Type:</strong> {log.eventType || log.action || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Severity:</strong> 
                                        <span className={`severity-badge severity-${log.severity || 'info'}`}>
                                          {log.severity ? log.severity.toUpperCase() : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="detail-item">
                                        <strong>Result:</strong> 
                                        <span className={`result-badge result-${log.result || 'success'}`}>
                                          {log.result || 'success'}
                                        </span>
                                      </div>
                                      <div className="detail-item">
                                        <strong>Timestamp:</strong> {formatTime(getLogTimestamp(log) || log.createdAt)}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Website:</strong> {website}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Plugin/Object:</strong> {pluginName}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Version:</strong> {version}
                                      </div>
                                      <div className="detail-item">
                                        <strong>User:</strong> {user}
                                      </div>
                                      <div className="detail-item">
                                        <strong>IP Address:</strong> {formatIP(log)}
                                      </div>
                                      <div className="detail-item">
                                        <strong>User Agent:</strong> {log.meta?.UserAgent || log.user_agent || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Session ID:</strong> {log.meta?.SessionID || log.session_id || 'N/A'}
                                      </div>
                                      <div className="detail-item full-width">
                                        <strong>Description:</strong>
                                        <p>{log.description || log.message || 'N/A'}</p>
                                      </div>
                                      {log.meta && Object.keys(log.meta).length > 0 && (
                                        <div className="detail-item full-width">
                                          <strong>Full Metadata:</strong>
                                          <pre className="metadata-pre">{JSON.stringify(log.meta, null, 2)}</pre>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>


              {/* Pagination */}
              {totalPages > 1 && (
                <div className="activity-log-pagination">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="pagination-button"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;

